import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

const REASONS = [
  { value: 'bonus', label: 'Bonus / Incentive' },
  { value: 'compensation', label: 'Compensation' },
  { value: 'penalty', label: 'Penalty' },
  { value: 'correction', label: 'Data Correction' },
  { value: 'damage', label: 'Damage Charges' },
  { value: 'refund_adjustment', label: 'Refund Adjustment' },
  { value: 'other', label: 'Other' }
];

function ManualAdjustments() {
  const [adjustments, setAdjustments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [deliveryPartners, setDeliveryPartners] = useState([]);

  // Form state
  const [form, setForm] = useState({
    recipientType: 'restaurant',
    recipientId: '',
    type: 'credit',
    amount: '',
    reason: 'bonus',
    description: ''
  });

  // Filter state
  const [filter, setFilter] = useState({ recipientType: '', type: '' });

  useEffect(() => {
    fetchAdjustments();
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const [restRes, dpRes] = await Promise.all([
        adminAPI.getAllRestaurants().catch(() => null),
        adminAPI.getAllDeliveryPartners().catch(() => null)
      ]);

      // Restaurants: response could be { data: { data: [...] } } or { data: { data: { restaurants: [...] } } }
      const restData = restRes?.data?.data;
      const restList = Array.isArray(restData) ? restData : (restData?.restaurants || restData?.data || []);
      setRestaurants(Array.isArray(restList) ? restList : []);

      // Delivery partners: response could be { data: { data: [...] } } or { data: { data: { partners: [...] } } }
      const dpData = dpRes?.data?.data;
      const dpList = Array.isArray(dpData) ? dpData : (dpData?.partners || dpData?.deliveryPartners || []);
      setDeliveryPartners(Array.isArray(dpList) ? dpList : []);
    } catch (e) {
      console.error('Failed to fetch recipients:', e);
      setRestaurants([]);
      setDeliveryPartners([]);
    }
  };

  const fetchAdjustments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.recipientType) params.recipientType = filter.recipientType;
      if (filter.type) params.type = filter.type;
      const res = await adminAPI.getManualAdjustments(params);
      const data = res?.data?.data || {};
      setAdjustments(data.adjustments || []);
      setSummary(data.summary || null);
    } catch (e) {
      console.error('Failed to fetch adjustments:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.recipientId || !form.amount || !form.description) {
      setMessage({ type: 'error', text: 'Please fill all fields' });
      return;
    }
    try {
      setCreating(true);
      setMessage(null);
      const res = await adminAPI.createManualAdjustment({
        ...form,
        amount: parseFloat(form.amount)
      });
      setMessage({ type: 'success', text: res?.data?.message || 'Adjustment created' });
      setForm(f => ({ ...f, amount: '', description: '', recipientId: '' }));
      fetchAdjustments();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to create adjustment' });
    } finally {
      setCreating(false);
    }
  };

  const handleReverse = async (id) => {
    if (!window.confirm('Are you sure you want to reverse this adjustment?')) return;
    try {
      await adminAPI.reverseManualAdjustment(id, { reason: 'Reversed by admin' });
      fetchAdjustments();
      setMessage({ type: 'success', text: 'Adjustment reversed' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to reverse' });
    }
  };

  const recipientOptions = form.recipientType === 'restaurant'
    ? (Array.isArray(restaurants) ? restaurants : []).map(r => ({ value: r._id, label: r.name }))
    : (Array.isArray(deliveryPartners) ? deliveryPartners : []).map(p => ({ value: p._id, label: p.name || p.phone }));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
        Manual Adjustments (Credit / Debit)
      </h1>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '8px',
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#991b1b',
          fontWeight: '500'
        }}>
          {message.text}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: '#dcfce7', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#166534' }}>Total Credits</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>+₹{summary.totalCredits?.toFixed(2)}</div>
          </div>
          <div style={{ background: '#fee2e2', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#991b1b' }}>Total Debits</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>-₹{summary.totalDebits?.toFixed(2)}</div>
          </div>
          <div style={{ background: '#e0e7ff', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#3730a3' }}>Net Adjustment</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: summary.netAdjustment >= 0 ? '#16a34a' : '#dc2626' }}>
              {summary.netAdjustment >= 0 ? '+' : ''}₹{summary.netAdjustment?.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Create New Adjustment</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Recipient Type</label>
              <select value={form.recipientType} onChange={e => setForm(f => ({ ...f, recipientType: e.target.value, recipientId: '' }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                <option value="restaurant">Restaurant</option>
                <option value="delivery">Delivery Partner</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Recipient</label>
              <select value={form.recipientId} onChange={e => setForm(f => ({ ...f, recipientId: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                <option value="">Select...</option>
                {recipientOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                <option value="credit">Credit (Add to balance)</option>
                <option value="debit">Debit (Deduct from balance)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Amount (₹)</label>
              <input type="number" step="0.01" min="0.01" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="Enter amount"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Reason</label>
              <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Description</label>
              <input type="text" value={form.description} maxLength={500}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the reason for this adjustment"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }} />
            </div>
          </div>
          <button type="submit" disabled={creating}
            style={{
              marginTop: '16px', padding: '10px 24px',
              background: form.type === 'credit' ? '#16a34a' : '#dc2626',
              color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
              opacity: creating ? 0.5 : 1
            }}>
            {creating ? 'Creating...' : form.type === 'credit' ? '+ Create Credit' : '- Create Debit'}
          </button>
        </form>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <select value={filter.recipientType} onChange={e => { setFilter(f => ({ ...f, recipientType: e.target.value })); setTimeout(fetchAdjustments, 0); }}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
          <option value="">All Types</option>
          <option value="restaurant">Restaurants</option>
          <option value="delivery">Delivery Partners</option>
        </select>
        <select value={filter.type} onChange={e => { setFilter(f => ({ ...f, type: e.target.value })); setTimeout(fetchAdjustments, 0); }}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
          <option value="">All (Credit & Debit)</option>
          <option value="credit">Credits Only</option>
          <option value="debit">Debits Only</option>
        </select>
      </div>

      {/* Adjustments Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : adjustments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No adjustments found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Recipient</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Created By</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map(adj => (
                <tr key={adj._id} style={{ borderBottom: '1px solid #e5e7eb', opacity: adj.status === 'reversed' ? 0.5 : 1 }}>
                  <td style={tdStyle}>{adj.adjustmentId}</td>
                  <td style={tdStyle}>{new Date(adj.createdAt).toLocaleDateString('en-IN')}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                      background: adj.type === 'credit' ? '#dcfce7' : '#fee2e2',
                      color: adj.type === 'credit' ? '#166534' : '#991b1b'
                    }}>
                      {adj.type === 'credit' ? '+ Credit' : '- Debit'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div>{adj.recipientName}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>{adj.recipientType}</div>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: '600', color: adj.type === 'credit' ? '#16a34a' : '#dc2626' }}>
                    {adj.type === 'credit' ? '+' : '-'}₹{adj.amount?.toFixed(2)}
                  </td>
                  <td style={tdStyle}>{REASONS.find(r => r.value === adj.reason)?.label || adj.reason}</td>
                  <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{adj.description}</td>
                  <td style={tdStyle}>{adj.createdBy?.name || adj.createdByName || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                      background: adj.status === 'active' ? '#dbeafe' : '#fef9c3',
                      color: adj.status === 'active' ? '#1e40af' : '#854d0e'
                    }}>
                      {adj.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {adj.status === 'active' && (
                      <button onClick={() => handleReverse(adj._id)}
                        style={{
                          padding: '4px 10px', background: '#fef3c7', color: '#92400e',
                          border: '1px solid #fcd34d', borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                        }}>
                        Reverse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' };
const tdStyle = { padding: '10px 12px', whiteSpace: 'nowrap' };

export default ManualAdjustments;
